import { useGetCurrentUserQuery, useGetToolUsageQuery } from "@/store/services/supabase";
import { useIsOwner } from "@/utils/hooks/useIsOwner";
import { ArrowRightOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/nextjs";
import { Alert, Button } from "antd";
import Link from "next/link";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
const DetailsButton = () => <Link href="/settings/org/usage" target="_blank" rel="noreferrer" data-sentry-element="Link" data-sentry-component="DetailsButton" data-sentry-source-file="SeveritiesFairUseWarning.tsx">
    <Button size="small" type="text" data-sentry-element="Button" data-sentry-source-file="SeveritiesFairUseWarning.tsx">
      Details {<ArrowRightOutlined />}
    </Button>
  </Link>;

/** Warns when the org is close to, or over, its Severities allocation, or its usage couldn't be read. */
export const SeveritiesFairUseWarning = () => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.severities.uploadLimitAlert"
  });
  const isOwner = useIsOwner();
  const role = isOwner ? "owner" : "user";
  const {
    data: currentUser
  } = useGetCurrentUserQuery();
  const {
    data: toolUsage,
    isError,
    error
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });

  // Reported once per failed request, rather than on every render.
  useEffect(() => {
    if (!error) return;
    Sentry.captureException("severitiesFairUseWarning", {
      tags: {
        section: "Quota Limit Error"
      },
      extra: {
        userId: currentUser?.id,
        error
      }
    });
  }, [error]);
  if (isError) {
    return <Alert type="error" className="mb-4" showIcon message={<Markdown>{t(`${role}.refresh`)}</Markdown>} />;
  }
  const usage = toolUsage?.severities;
  if (!usage) return null;

  // A quota of 0 reads as fully used, so it would otherwise show the limit-reached error.
  if (!usage.quota && !usage.alwaysEnabled) {
    return <Alert type="error" className="mb-4" showIcon action={isOwner && <DetailsButton />} message={<Markdown>{t(`${role}.noAllocation`)}</Markdown>} />;
  }
  if (usage.showWarning === "none") return null;
  return <Alert type={usage.showWarning === "warn" ? "warning" : "error"} className="mb-4" showIcon action={isOwner && <DetailsButton />} message={<Markdown>{t(`${role}.${usage.showWarning === "warn" ? "warning" : "error"}`)}</Markdown>} data-sentry-element="Alert" data-sentry-component="SeveritiesFairUseWarning" data-sentry-source-file="SeveritiesFairUseWarning.tsx" />;
};